import type { ModuleManifest, ModuleRoute, ModuleSearchProvider } from './types';
import type { RegistryEntry } from '../registries/types';

// The host discovers modules by globbing their manifests — this is the ONLY
// place the module folders are referenced, and it is a glob string (not an
// import statement), so removing a module folder simply yields fewer manifests
// and the platform still compiles (P18 §1 pluggability test).
const loaded = import.meta.glob('../../modules/*/index.ts', { eager: true }) as Record<
  string,
  { manifest?: ModuleManifest }
>;

export const MODULES: ModuleManifest[] = Object.values(loaded)
  .map((mod) => mod.manifest)
  .filter((m): m is ModuleManifest => Boolean(m))
  .sort((a, b) => a.key.localeCompare(b.key));

// Registry contributions collected from every mounted module. The shell merges
// these with its static registries (P16 §4) — a module adds a nav row by
// shipping a manifest, never by editing the shell.
export const moduleNavEntries = (): RegistryEntry[] => MODULES.flatMap((m) => m.nav ?? []);
export const moduleSettingsEntries = (): RegistryEntry[] => MODULES.flatMap((m) => m.settings ?? []);
export const moduleTileEntries = (): RegistryEntry[] => MODULES.flatMap((m) => m.tiles ?? []);
export const moduleNewMenuEntries = (): RegistryEntry[] => MODULES.flatMap((m) => m.newMenu ?? []);

// Search indexers (1E-1 consumes) and agent tool names (P09 parity) declared by
// modules — surfaced from manifests so the search core and agents service read
// them without knowing which modules exist.
export const moduleSearchProviders = (): ModuleSearchProvider[] => MODULES.flatMap((m) => m.search ?? []);
export const moduleAgentTools = (): string[] => MODULES.flatMap((m) => m.agentTools ?? []);

// G-21 · Entity → searchRegistry (DOD.md §4). The `ModuleManifest` union in
// ./types.ts proves at compile time that a manifest declaring `entities` also
// carries a `search` array; it cannot prove the two line up name-for-name
// (that's a runtime string comparison against whatever modules the glob
// above discovered). This closes that gap: every module's `entities` must
// each have a same-named row in `search` with both `indexer` and `renderer`
// present, or the module boot fails loudly instead of shipping an entity
// that's invisible to Cmd+K / universal search.
//
// This function — not a test file — is the CI enforcement point: the
// frontend has no unit-test runner (package.json only wires `tsc`, `vite
// build`, `eslint`), so "CI boots modules and asserts" (the gate's own
// words) is satisfied by running this assertion at host-boot, which happens
// on every `vite dev` start and inside the built bundle (this module is
// eager-imported the moment anything imports `MODULES`). If a unit-test
// runner lands later (e.g. vitest), promote this into `modules.test.ts`
// calling this same function against `MODULES` — the function itself
// doesn't change.
export function assertModuleSearchCoverage(modules: ModuleManifest[] = MODULES): void {
  for (const m of modules) {
    const entities = m.entities ?? [];
    if (entities.length === 0) continue;
    const registered = new Set((m.search ?? []).map((s) => s.entity));
    const missing = entities.filter((e) => !registered.has(e));
    if (missing.length > 0) {
      throw new Error(
        `[G-21] module "${m.key}" declares entity type(s) [${missing.join(', ')}] with no ` +
          'matching `search: [{ entity, indexer, renderer }]` registration ' +
          '(DOD.md §4 G-21, P16 §2, D42).'
      );
    }
    const incomplete = (m.search ?? []).filter((s) => s.indexer === undefined || s.renderer === undefined);
    if (incomplete.length > 0) {
      throw new Error(
        `[G-21] module "${m.key}" has search entr${incomplete.length === 1 ? 'y' : 'ies'} missing ` +
          `indexer/renderer for entity type(s) [${incomplete.map((s) => s.entity).join(', ')}] ` +
          '(DOD.md §4 G-21).'
      );
    }
  }
}

// Runs the moment this module is imported — i.e. on every host boot (dev
// server start, and inside the production bundle at runtime) — so a module
// that adds an entity without wiring search fails at boot, not in review.
assertModuleSearchCoverage();

// A route flattened with its owning module's key + feature keys, so the router
// layer can gate it against the capability document.
export interface HostRoute extends ModuleRoute {
  moduleKey: string;
  featureKeys: string[];
}

export const moduleRoutes = (): HostRoute[] =>
  MODULES.flatMap((m) => (m.routes ?? []).map((r) => ({ ...r, moduleKey: m.key, featureKeys: m.featureKeys })));
